import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Lightbulb } from 'lucide-react';
import logger from '../../utils/logger';

/**
 * Portfolio Insights Rollup — a calm, non-alarming row at the top of the
 * View Investments tab. Renders nothing when the user's portfolio is already
 * well-documented (server returns show=false).
 *
 * No numbers in the collapsed state. No urgency. No CTA. Clicking a flagged
 * investment does nothing — this is purely awareness, not an action prompt.
 *
 * Uses the Bearer token from localStorage (not cookies).
 */
const InsightsRollup = () => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch(`${API_URL}/api/portfolio/insights-rollup`, {
          headers: { 'Authorization': `Bearer ${token}` }, cache: 'no-store',
        });
        if (r.ok) setData(await r.json());
      } catch (err) {
        logger.error('Insights rollup load failed:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API_URL]);

  if (loading || !data || !data.show) return null;

  const lines = data.lines || [];

  return (
    <div
      className="border border-slate-200 bg-slate-50/60 rounded-lg mb-4"
      data-testid="insights-rollup"
    >
      <div className="px-4 pt-3 pb-1">
        <h2 className="text-lg sm:text-2xl font-serif font-bold text-slate-900" data-testid="insights-rollup-title">Portfolio Insights</h2>
      </div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-100/60 rounded-lg transition-colors"
        data-testid="insights-rollup-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Lightbulb className="w-4 h-4 text-slate-500 shrink-0" aria-hidden="true" />
          <span
            className="text-sm text-slate-700 truncate"
            data-testid="insights-rollup-summary"
          >
            {data.summary}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-slate-500 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" aria-hidden="true" />
        )}
      </button>
      {expanded && (
        <div
          className="px-4 pb-4 pt-1 border-t border-slate-200/70"
          data-testid="insights-rollup-expanded"
        >
          <p className="text-xs text-slate-500 mt-3 mb-2">{data.heading}</p>
          <ul className="space-y-1.5" data-testid="insights-rollup-list">
            {lines.map((line) => (
              <li
                key={line.type_key}
                className="flex items-start justify-between gap-3 py-1"
                data-testid={`insights-rollup-line-${line.type_key}`}
              >
                <div className="min-w-0 flex-1">
                  <span className="text-sm text-slate-800 font-medium">
                    {line.type_label}
                  </span>
                  <span className="text-xs text-slate-500 ml-1.5">
                    ({line.count})
                  </span>
                  <p className="text-xs text-slate-500 mt-0.5 italic">
                    {line.phrase}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default InsightsRollup;
